import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useWorkspace } from '../contexts/WorkspaceContext'
import { ScreenErrorBoundary } from './ScreenErrorBoundary'
import { TaskCentreButton, TaskCentreProvider } from './TaskCentre'
import { GlobalSearch } from './SearchPalette'
import { NavigationGuard, useLayer } from './layers'
import { useOnline } from '../lib/connection'
import { useTextSize } from '../lib/textSize'
import { ExporterNotices } from './ExportReviewNotices'
import { AccountPopup, TextSizeButtons } from './Appearance'
import { type Access, type Gate, STOCK, STOCK_PREVIEW, can, canOpen, useAccess } from '../lib/access'
import { CautionIcon, openQueriesText, useOpenQueries } from '../lib/openQueries'
import { currentRelease, describeRelease } from '../lib/release'
import { apiJson } from '../lib/api'
import { clearActAs } from '../lib/actAs'
import styles from './Shell.module.css'

interface ShellProps {
  children: React.ReactNode
}

interface NavRow {
  label: string
  // Where the row goes: the first screen here the caller can open. A row
  // with none is not on the rail at all (D1.7: No access is hidden).
  targets: { to: string; gate: Gate }[]
  // Every path the row stands for. A merged row (handoff §6, "Navigation load
  // across the whole product") is active for either of its screens.
  paths: string[]
  // The tooltip, where "Go to <label>" would not say enough.
  hint?: string
}

/* The rail, one definition for every screen (V4 navWorkforce / navOps /
   navStock / navSettings). No counts on any row: a number on the rail reads
   as a queue to clear (the S&D Overview carries its counts on the screen,
   not the row). The one mark is a caution on Pay while a picker's query is
   open, its count in the tooltip (lib/openQueries.tsx). The module's own settings are the last Configuration row,
   as in V4. */
const SECTIONS: { label: string; rows: NavRow[] }[] = [
  {
    label: 'Workforce',
    rows: [
      { label: 'Personnel', targets: [{ to: '/personnel', gate: 'personnel' }, { to: '/groups', gate: 'groups' }], paths: ['/personnel', '/groups'] },
      { label: 'Teams', targets: [{ to: '/teams', gate: 'teams' }], paths: ['/teams'] },
      // Wages is the landing screen; timesheets are where you go to fix
      // something the wage figures raised. Without Wages (OPERATIONS) the
      // row opens Timesheets.
      { label: 'Pay', targets: [{ to: '/wages', gate: 'wages' }, { to: '/timesheets', gate: 'timesheets' }], paths: ['/wages', '/timesheets', '/queries'] },
    ],
  },
  {
    label: 'Operations',
    rows: [
      {
        label: 'Product activity',
        targets: [{ to: '/product-activity', gate: 'product_activity' }, { to: '/batches', gate: 'batches' }],
        paths: ['/product-activity', '/batches'],
      },
      { label: 'Reports', targets: [{ to: '/reports', gate: 'reports' }], paths: ['/reports'] },
      { label: 'Label printing', targets: [{ to: '/label-printing', gate: 'label_printing' }], paths: ['/label-printing'] },
    ],
  },
  {
    label: 'Stock & Dispatch',
    rows: [
      { label: 'Overview', targets: [{ to: '/stock-dispatch/overview', gate: STOCK }], paths: ['/stock-dispatch/overview'] },
      { label: 'Stock', targets: [{ to: '/stock-dispatch', gate: STOCK }], paths: ['/stock-dispatch'] },
      { label: 'Orders', targets: [{ to: '/stock-dispatch/orders', gate: STOCK }], paths: ['/stock-dispatch/orders'] },
      { label: 'Dispatches', targets: [{ to: '/stock-dispatch/dispatches', gate: STOCK }], paths: ['/stock-dispatch/dispatches'] },
      // D2, §7o.2: a PackRight workspace without the licence has this one row,
      // for its ADMIN and SYSADMIN only; the four above need the licence.
      {
        label: 'Stock & Dispatch',
        targets: [{ to: '/stock-dispatch/preview', gate: STOCK_PREVIEW }],
        paths: ['/stock-dispatch/preview'],
        hint: 'Optional module — not in your subscription',
      },
    ],
  },
  {
    label: 'Configuration',
    rows: [
      { label: 'Sites & jobs', targets: [{ to: '/picking-jobs', gate: 'sites' }], paths: ['/picking-jobs', '/sites-jobs'] },
      { label: 'Devices', targets: [{ to: '/devices', gate: 'sites' }], paths: ['/devices'] },
      { label: 'Integrations', targets: [{ to: '/integrations', gate: 'integrations' }], paths: ['/integrations', '/integrations/api-keys'] },
      { label: 'Quality control', targets: [{ to: '/quality-control', gate: 'quality' }], paths: ['/quality-control'] },
      { label: 'General', targets: [{ to: '/settings', gate: 'general_settings' }], paths: ['/settings'] },
      { label: 'Stock & Dispatch', targets: [{ to: '/stock-dispatch/settings', gate: 'config_stock_dispatch' }], paths: ['/stock-dispatch/settings'] },
    ],
  },
]

// The rail as this caller sees it: each row pointed at the first of its
// screens they can open, rows with none left out, and sections left empty
// left out too.
export function railFor(access: Access | null) {
  return SECTIONS.map((section) => ({
    label: section.label,
    rows: section.rows.flatMap((row) => {
      const target = row.targets.find((t) => canOpen(access, t.gate))
      return target ? [{ label: row.label, to: target.to, paths: row.paths, hint: row.hint }] : []
    }),
  })).filter((section) => section.rows.length > 0)
}

// The role under the name on the user card.
const ROLE_NAMES: Record<string, string> = {
  ADMIN: 'Admin',
  OPERATIONS: 'Operations',
  PAYROLL: 'Payroll',
  REPORTING: 'Reporting',
  SYSADMIN: 'System admin',
}

// The Dataphyll leaf, as on the sign-in screen and the V4 rail.
export function BrandMark() {
  return (
    <svg className={styles.mark} viewBox="0 0 149 151" width="26" aria-hidden="true">
      <path d="M74.49,2.91H3.63V117.08c17.9-1.59,44.14-9.49,62.73-28.46,4.33-4.29,7.18-7.62,10.22-13.01h-.73c-10.3,14.59-25.38,22.47-39.38,28.21-2.99-9.33-6.99-27.06,9.82-41.79,5.49-4.82,10.99-6.82,16.31-8.76,7.78-2.84,15.82-5.78,25.38-17.73l1.5-1.87,1.67,1.72c16.13,16.68,17.97,46.17,3.86,61.88-12.7,14.14-29.67,18.09-41.66,18.09-4.36,0-9.3-.98-11.77-1.86-12.26,5.94-24.93,10.14-37.94,11.86v19.28H74.49c39.14,0,70.87-31.73,70.87-70.87S113.63,2.91,74.49,2.91Z" />
    </svg>
  )
}

// The task centre belongs to the workspace: the Shell is keyed on it, so a
// task started in one workspace never shows in another.
export function Shell({ children }: ShellProps) {
  return (
    <TaskCentreProvider>
      <ShellFrame>{children}</ShellFrame>
    </TaskCentreProvider>
  )
}

function ShellFrame({ children }: ShellProps) {
  const { user, signOut } = useAuth()
  const { currentWorkspace, subscriptions, openPicker } = useWorkspace()
  const access = useAccess()
  const rail = railFor(access)
  const openQueries = useOpenQueries()
  const showDash = can(access, 'dashboard')
  const location = useLocation()
  const multi = subscriptions.length > 1
  const online = useOnline()
  // Phone width only: the rail slides over the page and closes on arrival.
  const [railOpen, setRailOpen] = useState(false)
  useLayer(railOpen, () => setRailOpen(false))
  // The person's text size (h 1533-1574), applied to the whole app from here.
  const [textSize, setTextSize] = useTextSize(user)
  const [accountOpen, setAccountOpen] = useState(false)
  const accountBtn = useRef<HTMLButtonElement>(null)
  const closeAccount = useCallback(() => setAccountOpen(false), [])

  useEffect(() => {
    setRailOpen(false)
    setAccountOpen(false)
  }, [location.pathname])

  const handleSignOut = async () => {
    try {
      await signOut()
    } catch (err) {
      console.error('Sign-out failed:', err)
    }
  }

  const here = location.pathname.replace(/\/+$/, '') || '/'
  const isOn = (row: { paths: string[] }) => row.paths.includes(here)
  const onDash = here === '/dashboard'

  return (
    <div className={styles.container}>
      {railOpen && <div className={styles.scrim} onClick={() => setRailOpen(false)} aria-hidden="true" />}
      <nav className={`${styles.sidebar} ${railOpen ? styles.sidebarOpen : ''}`} aria-label="Main">
        {/* Brand block - workspace switcher. handoff §6: one element, the
            whole block is the button; the arrow and the hover exist only when
            there is somewhere to switch to. Otherwise it is inert and its
            title just names the workspace. */}
        {multi ? (
          <button
            type="button"
            className={`${styles.brandBlock} ${styles.brandSwitch}`}
            onClick={openPicker}
            title={`Switch workspace: you have access to ${subscriptions.length}`}
            aria-label={`${currentWorkspace?.name}. Switch workspace: you have access to ${subscriptions.length}`}
          >
            <BrandMark />
            <span className={styles.brandText}>
              <span className={styles.productName}>CONTROL</span>
              <span className={styles.workspaceLine}>
                <span className={styles.workspaceName}>{currentWorkspace?.name}</span>
                <svg className={styles.brandArrow} width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" aria-hidden="true">
                  <path d="m6 9 6 6 6-6" />
                </svg>
              </span>
            </span>
          </button>
        ) : (
          <div className={styles.brandBlock} title={`${currentWorkspace?.name}: your only workspace`}>
            <BrandMark />
            <span className={styles.brandText}>
              <span className={styles.productName}>CONTROL</span>
              <span className={styles.workspaceLine}>
                <span className={styles.workspaceName}>{currentWorkspace?.name}</span>
              </span>
            </span>
          </div>
        )}

        {/* handoff §6b: global search, under the brand, every page. */}
        <GlobalSearch />

        <div className={styles.navSections}>
          {showDash && (
          <Link
            to="/dashboard"
            className={`${styles.navItem} ${onDash ? styles.active : ''}`}
            aria-current={onDash ? 'page' : undefined}
            title={onDash ? 'You are here' : 'Go to Dashboard'}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round" aria-hidden="true">
              <rect x="3" y="3" width="7" height="9" rx="1.5" />
              <rect x="14" y="3" width="7" height="5" rx="1.5" />
              <rect x="14" y="12" width="7" height="9" rx="1.5" />
              <rect x="3" y="16" width="7" height="5" rx="1.5" />
            </svg>
            Dashboard
          </Link>
          )}

          {rail.map((section) => (
            <div key={section.label} className={styles.navSection}>
              <h3 className={styles.sectionLabel}>{section.label}</h3>
              <ul className={styles.navList}>
                {section.rows.map((row) => {
                  const on = isOn(row)
                  const caution = openQueries > 0 && row.paths.includes('/queries')
                  return (
                    <li key={row.label}>
                      <Link
                        to={row.to}
                        className={`${styles.navItem} ${on ? styles.active : ''}`}
                        aria-current={on ? 'page' : undefined}
                        title={caution ? openQueriesText(openQueries) : on ? 'You are here' : row.hint || `Go to ${row.label}`}
                      >
                        <span className={styles.dot} aria-hidden="true" />
                        {row.label}
                        {caution && <CautionIcon title={openQueriesText(openQueries)} className={styles.caution} />}
                      </Link>
                    </li>
                  )
                })}
              </ul>
            </div>
          ))}
        </div>

        {/* handoff §10.4: the task centre, bottom of the rail, every page. */}
        <TaskCentreButton />

        {/* h 1536: the text size, at the foot of the rail above the user chip. */}
        <TextSizeButtons size={textSize} onChange={setTextSize} />

        {/* User card at the foot (V4): initials, name, role. The whole card
            opens Appearance and Account, which is where Sign out lives. */}
        <button
          ref={accountBtn}
          type="button"
          className={styles.userChip}
          onClick={() => setAccountOpen((o) => !o)}
          aria-haspopup="dialog"
          aria-expanded={accountOpen}
          title="Appearance and account"
        >
          <span className={styles.userInitials} aria-hidden="true">
            {user?.displayName
              ?.split(' ')
              .map((n: string) => n[0])
              .join('')
              .slice(0, 2)
              .toUpperCase() || 'U'}
          </span>
          <span className={styles.userInfo}>
            <span className={styles.userName}>{user?.displayName || user?.email || 'User'}</span>
            {access?.role && <span className={styles.userRole}>{ROLE_NAMES[access.role] || access.role}</span>}
          </span>
        </button>
        <ReleaseNote />
        {accountOpen && (
          <AccountPopup
            anchor={accountBtn}
            name={user?.displayName || user?.email || 'User'}
            email={user?.email || ''}
            workspace={currentWorkspace?.name || ''}
            size={textSize}
            onSize={setTextSize}
            onSignOut={handleSignOut}
            onClose={closeAccount}
          />
        )}
      </nav>

      <div className={styles.mainArea}>
        {/* Phone width only (CSS): the design has no top bar, but a rail that
            is off-canvas needs something to call it back. */}
        <header className={styles.phoneBar}>
          <button
            type="button"
            className={styles.menuButton}
            onClick={() => setRailOpen(true)}
            aria-label="Open menu"
            aria-expanded={railOpen}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.75" strokeLinecap="round" aria-hidden="true">
              <path d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <span className={styles.phoneTitle}>{currentWorkspace?.name}</span>
        </header>

        {/* h 4071-4073: across the top of the app while the connection is
            gone. Writes are refused at the call site (apiJson), so this only
            says what that means. */}
        {!online && (
          <div className={styles.offline} role="status" data-testid="offline-banner">
            <strong>You are offline.</strong> Reading still works from what is on screen. Anything that saves is refused until the connection is back, and nothing you have typed is lost.
          </div>
        )}

        <ActingBanner />

        {/* D6.5: the person who produced an export that is now being recalculated. */}
        {can(access, 'wages') && <ExporterNotices />}

        {/* Keyed on the path, so moving to another screen clears a crash. */}
        <main className={styles.pageContent}>
          <ScreenErrorBoundary key={here}>{children}</ScreenErrorBoundary>
        </main>
        <NavigationGuard />

      </div>
    </div>
  )
}

// A SYSADMIN acting as a system user (lib/actAs.ts): across the top, whom, and the way back. Stop forgets it first,
// so the stop is recorded as the SYSADMIN, not as the user.
export function ActingBanner() {
  const { actingAs } = useWorkspace()
  const { user } = useAuth()
  const [busy, setBusy] = useState(false)
  if (!actingAs) return null
  const stop = async () => {
    setBusy(true)
    clearActAs()
    try {
      await apiJson(user, '/api/impersonation/stop', { body: { membershipId: actingAs.membershipId } })
    } catch {
      // Acting has stopped in this tab either way; only the record of it is missing.
    }
    window.location.assign('/')
  }
  return (
    <div className={styles.acting} role="status" data-testid="acting-banner">
      <span>
        <strong>Acting as {actingAs.name || actingAs.email}</strong> ({actingAs.permission_set}). You see and do what they may; every change
        is recorded as made by you.
      </span>
      <button type="button" className={styles.actingStop} disabled={busy} onClick={stop}>
        {busy ? 'Stopping�' : 'Stop acting'}
      </button>
    </div>
  )
}

// Which release this is and which database it uses, so nobody mistakes UAT for production (or a stale tab for the
// latest release). UAT is marked in amber.
const RELEASE = describeRelease(currentRelease())
const RELEASE_ENV = currentRelease().env
export function ReleaseNote() {
  return (
    <p className={`${styles.release} ${RELEASE_ENV && RELEASE_ENV !== 'production' ? styles.releaseTest : ''}`}>
      <span className={styles.releaseWhere}>{RELEASE.where}</span>
      {RELEASE.when && <span>{RELEASE.when}</span>}
    </p>
  )
}
